import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  AlertCircle,
  Clock,
  Loader2,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Calendar,
  DollarSign,
  FileText,
  Receipt,
  CreditCard,
  Search,
  ChevronRight,
  Lock,
  Crown
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import UsageLimitsWidget from "@/components/UsageLimitsWidget";

import { API_URL } from "@/utils/api";

// Lime green theme colors
const LIME = "#BFFF00";
const DARK = "#121212";
const CHART_COLORS = [LIME, "#10b981", "#121212", "#666666", "#8b5cf6"];

// Plan feature access mapping
const PLAN_ACCESS = {
  free: {
    invoicing: true,  // Basic invoice generator
    documents: true,
    contracts: false,
  },
  growth: {
    invoicing: true,
    documents: true,
    contracts: true,
  },
  scale: {
    invoicing: true,
    documents: true,
    contracts: true,
  },
  enterprise: {
    invoicing: true,
    documents: true,
    contracts: true,
  }
};

// Plan display info
const PLAN_INFO = {
  free: { name: "Free / Starter", features: ["Up to $25K annual transactions", "Basic bookkeeping", "Invoice generator", "Expense tracking"] },
  growth: { name: "Growth", features: ["Up to $100K annual transactions", "Full bookkeeping + reconciliation", "KPI dashboards", "Dedicated bookkeeper"] },
  scale: { name: "Scale", features: ["Unlimited transactions", "Advanced reporting", "Tax strategy planning", "Priority support"] },
  enterprise: { name: "Enterprise", features: ["Dedicated team of bookkeepers", "Custom integrations", "White-glove onboarding"] }
};

export default function DashboardHomePage({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [kpiData, setKpiData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState("");
  const [requiredPlan, setRequiredPlan] = useState("");
  const navigate = useNavigate();

  // Get user's current plan and access rights
  const userPlan = user?.plan || "free";
  const planAccess = PLAN_ACCESS[userPlan] || PLAN_ACCESS.free;
  const isPaidUser = userPlan && userPlan !== "free";
  
  // Check if user has access to a feature
  const hasAccess = (featureKey) => {
    return planAccess[featureKey] === true;
  };
  
  // Get the minimum required plan for a feature
  const getRequiredPlan = (featureKey) => {
    for (const plan of ["free", "growth", "scale", "enterprise"]) {
      if (PLAN_ACCESS[plan][featureKey]) {
        return plan;
      }
    }
    return "growth";
  };

  const handleQuickAction = (path, featureName, featureKey) => {
    if (!hasAccess(featureKey)) {
      setBlockedFeature(featureName);
      setRequiredPlan(getRequiredPlan(featureKey));
      setShowUpgradeModal(true);
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const [invoicesRes, kpiRes, transactionsRes] = await Promise.all([
        fetch(`${API_URL}/api/invoices`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/kpi/overview`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/transactions`, { credentials: "include", headers })
      ]);

      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (kpiRes.ok) setKpiData(await kpiRes.json());
      if (transactionsRes.ok) setTransactions(await transactionsRes.json());
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate invoice stats
  const invoiceStats = {
    overdue: { count: invoices.filter(i => i.status === "overdue").length, amount: invoices.filter(i => i.status === "overdue").reduce((sum, i) => sum + (i.amount || 0), 0) },
    open: { count: invoices.filter(i => i.status === "pending" || i.status === "open").length, amount: invoices.filter(i => i.status === "pending" || i.status === "open").reduce((sum, i) => sum + (i.amount || 0), 0) },
    processing: { count: invoices.filter(i => i.status === "processing").length, amount: invoices.filter(i => i.status === "processing").reduce((sum, i) => sum + (i.amount || 0), 0) },
    paid: { count: invoices.filter(i => i.status === "paid").length, amount: invoices.filter(i => i.status === "paid").reduce((sum, i) => sum + (i.amount || 0), 0) },
  };

  // Get due invoices
  const dueInvoices = invoices
    .filter(i => i.status !== "paid")
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 5);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#BFFF00]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#121212] font-['Manrope']">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-[#121212]/60 mt-1">Here's an overview of your financial activity.</p>
        </div>

        {/* Usage Limits Widget */}
        <UsageLimitsWidget user={user} />

        {/* Invoice Overview Cards */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-[#121212] font-['Manrope']">Invoice Overview</h2>
            <Link to="/dashboard/invoicing">
              <Button variant="ghost" className="text-[#121212] hover:text-[#121212] hover:bg-[#BFFF00]/20 gap-1 font-medium">
                View All <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Overdue */}
            <Card className="border-2 border-dashed border-red-200 hover:border-red-400 hover:shadow-lg transition-all cursor-pointer bg-white rounded-2xl">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-medium text-[#121212]/60">Overdue</span>
                    </div>
                    <p className="text-2xl font-bold text-red-600 font-['Manrope']">{formatCurrency(invoiceStats.overdue.amount)}</p>
                    <p className="text-xs text-[#121212]/50 mt-1">{invoiceStats.overdue.count} invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Open */}
            <Card className="border-2 border-dashed border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer bg-white rounded-2xl">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium text-[#121212]/60">Open</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-600 font-['Manrope']">{formatCurrency(invoiceStats.open.amount)}</p>
                    <p className="text-xs text-[#121212]/50 mt-1">{invoiceStats.open.count} invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Processing */}
            <Card className="border-2 border-dashed border-[#BFFF00]/50 hover:border-[#BFFF00] hover:shadow-lg transition-all cursor-pointer bg-white rounded-2xl">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Loader2 className="w-4 h-4 text-[#121212]" />
                      <span className="text-sm font-medium text-[#121212]/60">Processing</span>
                    </div>
                    <p className="text-2xl font-bold text-[#121212] font-['Manrope']">{formatCurrency(invoiceStats.processing.amount)}</p>
                    <p className="text-xs text-[#121212]/50 mt-1">{invoiceStats.processing.count} invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Paid */}
            <Card className="border-2 border-dashed border-green-200 hover:border-green-400 hover:shadow-lg transition-all cursor-pointer bg-white rounded-2xl">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium text-[#121212]/60">Paid</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600 font-['Manrope']">{formatCurrency(invoiceStats.paid.amount)}</p>
                    <p className="text-xs text-[#121212]/50 mt-1">{invoiceStats.paid.count} invoices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Latest Due Invoices */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-[#121212] font-['Manrope']">Latest Due Invoices</h2>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#121212]/40" />
              <Input
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 bg-[#F7FFE5] border-[#BFFF00]/30 rounded-xl focus:ring-2 focus:ring-[#BFFF00]/50 focus:border-[#BFFF00]"
              />
            </div>
          </div>
          
          <Card className="border-2 border-dashed border-[#BFFF00]/30 overflow-hidden rounded-2xl bg-white">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F7FFE5] border-b border-[#BFFF00]/20">
                  <tr>
                    <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Invoice</th>
                    <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Client</th>
                    <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Due Date</th>
                    <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Amount</th>
                    <th className="text-left text-xs font-semibold text-[#121212]/60 uppercase px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#BFFF00]/10">
                  {dueInvoices.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-12">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mx-auto mb-3 border-2 border-dashed border-[#BFFF00]/50">
                            <FileText className="w-6 h-6 text-[#121212]/40" />
                          </div>
                          <p className="font-bold text-[#121212] font-['Manrope']">No invoices yet</p>
                          <p className="text-sm text-[#121212]/60 mt-1">Create your first invoice to get started.</p>
                          <Link to="/dashboard/invoicing">
                            <Button className="mt-4 bg-[#121212] hover:bg-[#2a2a2a] rounded-full px-6 font-semibold">
                              Create Invoice
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    dueInvoices.map((invoice) => (
                      <tr key={invoice.invoice_id} className="hover:bg-[#F7FFE5]/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#F7FFE5] rounded-xl flex items-center justify-center">
                              <Receipt className="w-4 h-4 text-[#121212]" />
                            </div>
                            <span className="font-medium text-[#121212]">#{invoice.invoice_number || invoice.invoice_id}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[#121212]/70">{invoice.client_name || "Client"}</td>
                        <td className="px-4 py-3 text-[#121212]/70">
                          {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : "-"}
                        </td>
                        <td className="px-4 py-3 font-bold text-[#121212]">{formatCurrency(invoice.amount)}</td>
                        <td className="px-4 py-3">
                          <Badge 
                            className={`rounded-full ${
                              invoice.status === "paid" ? "bg-green-100 text-green-700" :
                              invoice.status === "overdue" ? "bg-red-100 text-red-700" :
                              invoice.status === "processing" ? "bg-[#BFFF00]/30 text-[#121212]" :
                              "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {invoice.status}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {/* Revenue & Expenses Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Revenue Trend */}
          <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl bg-white">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-bold font-['Manrope']">Revenue Trend</CardTitle>
                <Badge className="bg-[#BFFF00]/20 text-[#121212] rounded-full border border-[#BFFF00]/50">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  +12%
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={kpiData?.monthly_data || []}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#BFFF00" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#BFFF00" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#BFFF00" strokeOpacity={0.2} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#121212" strokeOpacity={0.4} />
                    <YAxis tick={{ fontSize: 12 }} stroke="#121212" strokeOpacity={0.4} tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#121212" 
                      fill="url(#revenueGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Expense Categories */}
          <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-bold font-['Manrope']">Expense Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex items-center justify-center">
                {kpiData?.expense_categories?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={kpiData.expense_categories}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {kpiData.expense_categories.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend 
                        layout="vertical" 
                        align="right" 
                        verticalAlign="middle"
                        iconType="circle"
                        iconSize={8}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-[#121212]/40">
                    <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No expense data yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-[#121212] font-['Manrope']">Quick Actions</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div onClick={() => handleQuickAction("/dashboard/invoicing", "Invoicing", "invoicing")} className="cursor-pointer">
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all group rounded-2xl bg-white relative">
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center mb-3 group-hover:bg-[#BFFF00] transition-colors">
                    <FileText className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] font-['Manrope']">Create Invoice</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Send invoices to clients</p>
                </CardContent>
              </Card>
            </div>
            <div onClick={() => handleQuickAction("/dashboard/contracts", "Contracts", "contracts")} className="cursor-pointer">
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all group rounded-2xl bg-white relative">
                {!hasAccess("contracts") && (
                  <div className="absolute top-2 right-2">
                    <Lock className="w-4 h-4 text-[#121212]/40" />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center mb-3 group-hover:bg-[#BFFF00] transition-colors">
                    <Receipt className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] font-['Manrope']">New Contract</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Create legal contracts</p>
                </CardContent>
              </Card>
            </div>
            <div onClick={() => handleQuickAction("/dashboard/documents", "Documents", "documents")} className="cursor-pointer">
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all group rounded-2xl bg-white">
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center mb-3 group-hover:bg-[#BFFF00] transition-colors">
                    <DollarSign className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] font-['Manrope']">Upload Statement</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Import bank statements</p>
                </CardContent>
              </Card>
            </div>
            <a href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ07qBLS9ridhleyYbHgoh8tK2GytA06zF8p0i5zF3IfiDRKXQGW17EZmfPsPcjIyZXiw-BjNNVw" target="_blank" rel="noopener noreferrer">
              <Card className="border-2 border-dashed border-gray-200 hover:border-[#BFFF00] hover:shadow-lg hover:shadow-[#BFFF00]/10 transition-all cursor-pointer group rounded-2xl bg-white">
                <CardContent className="p-4">
                  <div className="w-10 h-10 bg-[#F7FFE5] rounded-xl flex items-center justify-center mb-3 group-hover:bg-[#BFFF00] transition-colors">
                    <Calendar className="w-5 h-5 text-[#121212]" />
                  </div>
                  <h3 className="font-bold text-[#121212] font-['Manrope']">Book a Call</h3>
                  <p className="text-sm text-[#121212]/60 mt-1">Talk to our team</p>
                </CardContent>
              </Card>
            </a>
          </div>
        </div>

        {/* Upgrade Modal */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <div className="w-14 h-14 bg-[#F7FFE5] rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-[#BFFF00]">
                <Crown className="w-7 h-7 text-[#121212]" />
              </div>
              <DialogTitle className="text-xl font-bold text-center font-['Manrope']">
                Upgrade to Access {blockedFeature}
              </DialogTitle>
              <DialogDescription className="text-center text-[#121212]/60">
                This feature requires the <span className="font-semibold text-[#121212] capitalize">{requiredPlan || "Growth"}</span> plan or higher.
              </DialogDescription>
            </DialogHeader>
            
            {/* Required Plan Features */}
            {requiredPlan && PLAN_INFO[requiredPlan] && (
              <div className="p-4 bg-[#F7FFE5] rounded-2xl border-2 border-dashed border-[#BFFF00]/50 mt-2">
                <p className="font-semibold text-[#121212] text-sm mb-2 capitalize">{PLAN_INFO[requiredPlan].name} includes:</p>
                <ul className="space-y-1.5">
                  {PLAN_INFO[requiredPlan].features.slice(0, 3).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[#121212]/70">
                      <div className="w-1.5 h-1.5 bg-[#BFFF00] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex flex-col gap-3 mt-4">
              <Link to="/pricing">
                <Button className="w-full bg-[#121212] hover:bg-[#2a2a2a] rounded-full font-semibold">
                  View Plans & Pricing
                </Button>
              </Link>
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="rounded-full">
                Maybe Later
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
